import { Component, h, Prop, Watch, Host, Element } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { DEFAULT_TRACKING_DELAY, delayPageChange } from '../../../global/utility/tracking-helpers';
import { getFromDay, getLocaleTimeWithSuffix, getToDay } from '../../../helper';
import { Weekday } from '../../../types/weekday';

export interface UiCmsContactChannelsPhoneBody {
  fromDay: Weekday;
  fromTime: string;
  number: string;
  toDay: Weekday;
  toTime: string;
  buttonTitle: string;
  headline: string;
  text: string;
}

@Component({
  tag: 'ui-cms-contact-channels-phone',
  styleUrl: 'ui-cms-contact-channels-phone.scss',
})
export class UiCmsContactChannelsPhone {
  @Element() el: HTMLUiCmsContactChannelsPhoneElement;

  @Prop({ mutable: true }) body: UiCmsContactChannelsPhoneBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Prop() inverted: boolean;

  @Prop({ mutable: true }) i18n: { [key: string]: string } = {
    phone: 'Telefon',
    fromMonday: 'Montag',
    fromTuesday: 'Dienstag',
    fromWednesday: 'Mittwoch',
    fromThursday: 'Donnerstag',
    fromFriday: 'Freitag',
    fromSaturday: 'Samstag',
    fromSunday: 'Sonntag',
    fromTime: 'von',
    toMonday: 'Montag',
    toTuesday: 'Dienstag',
    toWednesday: 'Mittwoch',
    toThursday: 'Donnerstag',
    toFriday: 'Freitag',
    toSaturday: 'Samstag',
    toSunday: 'Sonntag',
    toTime: 'bis',
    toWeekday: 'bis',
  };

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Watch('utilityI18n')
  i18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    this.i18nChangeHandler(this.utilityI18n);
  }

  onLinkClick() {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Contact Phone',
        eventType: 'Action',
        eventCaller: this.el,
      },
      attributes: {
        elementType: 'Link',
        elementLabel: this.body.number,
        externalTarget: this.body.number,
      },
      category: {
        area: 'Content',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  onButtonClick(event: Event, link: HTMLAnchorElement) {
    delayPageChange(DEFAULT_TRACKING_DELAY, event, link);
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Contact Phone',
        eventType: 'Action',
        eventCaller: this.el,
      },
      attributes: {
        elementType: 'Link',
        elementLabel: this.body.buttonTitle,
        externalTarget: this.body.number,
      },
      category: {
        area: 'Content',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  render() {
    const link = `tel:${this.body.number}`;
    return (
      <Host class="channel">
        <div class="channel-contactbox">
          <div class="up">
            <h4
              class={{
                'sd-headline': true,
                'sd-headline--inline': true,
                'sd-headline--size-3xl': true,
                'sd-headline--inverted': this.inverted,
              }}
            >
              <sd-icon
                library={window.ui.cmsState.iconLibrary}
                name="union-investment/content/phone-receiver"
                color={this.inverted ? 'white' : 'primary'}
              ></sd-icon>
              <span innerHTML={this.body.headline}></span>
            </h4>
            <p
              class={{ copy: true, 'sd-paragraph': true, 'sd-paragraph--inverted': this.inverted }}
              innerHTML={this.body.text}
            ></p>
            <time itemProp="openingHours">
              {`${this.i18n[getFromDay(this.body.fromDay)]} `}
              {this.i18n?.toWeekday}
              {` ${this.i18n[getToDay(this.body.toDay)]} `}
              {this.i18n?.fromTime}
              {` ${getLocaleTimeWithSuffix(this.body.fromTime)} `}
              {this.i18n?.toTime}
              {` ${getLocaleTimeWithSuffix(this.body.toTime)} `}
            </time>
            <br />
            <br />
            <sd-link
              class="copy phone"
              inverted={this.inverted}
              size="inherit"
              href={link}
              onClick={this.onLinkClick.bind(this)}
            >
              <meta itemProp="telephone" content={this.body.number} />
              <strong>
                {this.i18n?.phone || 'Telefon'}: {this.body.number}
              </strong>
            </sd-link>
          </div>
          <div class="down">
            <sd-button
              href={link}
              onClick={(event: Event) => this.onButtonClick(event, event.currentTarget as HTMLAnchorElement)}
              className="button"
              variant="primary"
              inverted={this.inverted}
              size="lg"
              type="button"
            >
              <meta itemProp="telephone" content={this.body.number} />
              {this.body.buttonTitle}
            </sd-button>
          </div>
        </div>
      </Host>
    );
  }
}
